import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, IconButton, Collapse, CircularProgress,
  Pagination, Alert, Tooltip, Divider, Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';

const STATUS_OPTIONS = [
  { value: '', label: 'All Patients' },
  { value: 'errors', label: 'With Errors' },
  { value: 'abandoned', label: 'Abandoned' },
  { value: 'completed', label: 'Completed' },
];

const EVENT_COLORS = {
  form_started: '#2196f3',
  step_started: '#90caf9',
  step_completed: '#4caf50',
  step_validation_error: '#ff9800',
  step_save_error: '#f44336',
  submit_started: '#1976d2',
  submit_pdf_error: '#d32f2f',
  submit_api_error: '#d32f2f',
  submit_completed: '#388e3c',
  form_abandoned: '#9e9e9e',
  network_error: '#e91e63',
  browser_error: '#9c27b0',
};

const maskEmail = (email) => {
  if (!email) return '—';
  return email.replace(/(.{2}).*(@.*)/, '$1***$2');
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const fmtDuration = (ms) => {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const IPHONE_BY_SCREEN = {
  '320x568': 'iPhone 5/SE 1st',
  '375x667': 'iPhone 6/7/8/SE',
  '414x736': 'iPhone 6+/7+/8+',
  '375x812': 'iPhone X/XS/11 Pro/12 Mini',
  '414x896': 'iPhone XR/XS Max/11',
  '390x844': 'iPhone 12/13/14',
  '393x852': 'iPhone 14 Pro/15/16',
  '428x926': 'iPhone 14 Plus',
  '430x932': 'iPhone 14 Pro Max/15 Plus/15 Pro Max',
  '402x874': 'iPhone 16 Pro',
  '440x956': 'iPhone 16 Pro Max',
  '412x915': 'iPhone 17',
};

const IPAD_BY_SCREEN = {
  '768x1024': 'iPad (Standard)',
  '744x1133': 'iPad Mini',
  '810x1080': 'iPad 10th Gen',
  '820x1180': 'iPad Air',
  '834x1194': 'iPad Pro 11"',
  '1024x1366': 'iPad Pro 12.9"',
};

const SAMSUNG_MODELS = {
  'SM-S918': 'Galaxy S23 Ultra', 'SM-S916': 'Galaxy S23+', 'SM-S911': 'Galaxy S23',
  'SM-S928': 'Galaxy S24 Ultra', 'SM-S926': 'Galaxy S24+', 'SM-S921': 'Galaxy S24',
  'SM-S938': 'Galaxy S25 Ultra', 'SM-S936': 'Galaxy S25+', 'SM-S931': 'Galaxy S25',
  'SM-A546': 'Galaxy A54', 'SM-A556': 'Galaxy A55', 'SM-A156': 'Galaxy A15',
  'SM-G991': 'Galaxy S21', 'SM-G996': 'Galaxy S21+', 'SM-G998': 'Galaxy S21 Ultra',
  'SM-S901': 'Galaxy S22', 'SM-S906': 'Galaxy S22+', 'SM-S908': 'Galaxy S22 Ultra',
  'SM-F946': 'Galaxy Z Fold5', 'SM-F731': 'Galaxy Z Flip5',
  'SM-F956': 'Galaxy Z Fold6', 'SM-F741': 'Galaxy Z Flip6',
};

const parseDevice = (deviceInfo) => {
  if (!deviceInfo) return { type: '—', browser: '—', screen: '—', model: '—' };
  const ua = deviceInfo.userAgent || '';
  const screenSize = deviceInfo.screen || '';
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua);

  let browser = 'Unknown';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\//i.test(ua)) browser = 'Opera';
  else if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) browser = 'Chrome';
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';

  let os = 'Unknown';
  if (/Windows NT 10/i.test(ua)) os = 'Windows 10/11';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad/i.test(ua)) os = 'iOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  let model = '';
  if (/iPhone/i.test(ua)) {
    model = IPHONE_BY_SCREEN[screenSize] || 'iPhone';
  } else if (/iPad/i.test(ua)) {
    model = IPAD_BY_SCREEN[screenSize] || 'iPad';
  } else if (/Android/i.test(ua)) {
    const m = ua.match(/;\s*([^;)]+?)(?:\s+Build\/|\))/);
    let rawModel = (m && m[1] && !/Android/i.test(m[1])) ? m[1].trim() : '';
    if (rawModel) {
      const smMatch = rawModel.match(/SM-[A-Z]\d{3}/);
      if (smMatch && SAMSUNG_MODELS[smMatch[0]]) {
        model = `Samsung ${SAMSUNG_MODELS[smMatch[0]]}`;
      } else if (/Pixel/i.test(rawModel)) {
        model = rawModel;
      } else if (/SAMSUNG|SM-/i.test(rawModel)) {
        model = `Samsung ${rawModel.replace(/SAMSUNG\s*/i, '')}`;
      } else {
        model = rawModel;
      }
    } else {
      model = 'Android Device';
    }
  } else if (/Macintosh/i.test(ua)) {
    model = 'Mac';
  } else if (/Windows/i.test(ua)) {
    model = 'Windows PC';
  } else {
    model = 'Unknown';
  }

  const type = isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop';
  return { type, browser, os, model, screen: screenSize || '—' };
};

function PatientTimeline({ email, tenantId }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/api/activity-log/admin/timeline', {
          params: { tenantId, email }
        });
        if (!cancelled) setTimeline(res.data.timeline || []);
      } catch {
        if (!cancelled) setTimeline([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [email, tenantId]);

  if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (!timeline.length) return <Box sx={{ p: 2 }}><Typography color="text.secondary">No events found</Typography></Box>;

  const groupMap = {};
  for (const evt of timeline) {
    if (!groupMap[evt.session_id]) {
      groupMap[evt.session_id] = { sessionId: evt.session_id, appSource: evt.app_source, events: [] };
    }
    groupMap[evt.session_id].events.push(evt);
  }
  const chronological = Object.values(groupMap);
  chronological.forEach((g, i) => { g.number = i + 1; });
  const sessionGroups = [...chronological].reverse();

  return (
    <Box sx={{ p: 2, pl: 4 }}>
      {sessionGroups.map((group, gi) => {
        const firstEvt = group.events[0];
        const lastEvt = group.events[group.events.length - 1];
        const hasError = group.events.some(e => e.event_type.includes('error'));
        const submitted = group.events.some(e => e.event_type === 'submit_completed');
        const abandoned = group.events.some(e => e.event_type === 'form_abandoned');

        let statusLabel = 'In Progress';
        let statusColor = '#ff9800';
        if (submitted) { statusLabel = 'Completed'; statusColor = '#4caf50'; }
        else if (abandoned) { statusLabel = 'Abandoned'; statusColor = '#9e9e9e'; }
        else if (hasError) { statusLabel = 'Error'; statusColor = '#f44336'; }

        const appLabel = { client: 'Client', kiosk: 'Kiosk', followup: 'Follow-up' }[group.appSource] || group.appSource;
        const deviceEvt = group.events.find(e => e.device_info && Object.keys(e.device_info).length > 0);
        const device = parseDevice(deviceEvt?.device_info);

        return (
          <Box key={group.sessionId} sx={{ mb: 2 }}>
            {gi > 0 && <Divider sx={{ mb: 2 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Session {group.number}
              </Typography>
              <Chip label={appLabel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              <Chip
                label={statusLabel}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', backgroundColor: statusColor + '20', color: statusColor }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {fmtDate(firstEvt.created_at)} → {fmtDate(lastEvt.created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, ml: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label={`${device.type} · ${device.model}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', color: '#555' }}
              />
              <Chip
                label={`${device.os} · ${device.browser}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', color: '#555' }}
              />
              <Chip
                label={`${device.screen}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', color: '#555' }}
              />
            </Box>
            <Box sx={{ borderLeft: '2px solid #e0e0e0', pl: 2, ml: 1 }}>
              {group.events.map((evt, i) => {
                const isError = evt.event_type.includes('error') || evt.event_type.includes('fail');
                return (
                  <Box key={evt.id || i} sx={{ mb: 1, position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute', left: -19, top: 4,
                      width: 10, height: 10, borderRadius: '50%',
                      backgroundColor: EVENT_COLORS[evt.event_type] || '#999',
                      border: '2px solid #fff',
                    }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={evt.event_type.replace(/_/g, ' ')}
                        size="small"
                        sx={{
                          backgroundColor: EVENT_COLORS[evt.event_type] || '#999',
                          color: '#fff', fontWeight: 500, fontSize: '0.7rem', height: 20,
                        }}
                      />
                      {evt.step_number && (
                        <Typography variant="caption" color="text.secondary">
                          Step {evt.step_number}{evt.step_name ? `: ${evt.step_name}` : ''}
                        </Typography>
                      )}
                      {evt.duration_ms > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({fmtDuration(evt.duration_ms)})
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {fmtDate(evt.created_at)}
                      </Typography>
                    </Box>
                    {isError && evt.error_message && (
                      <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.25, ml: 1 }}>
                        {evt.error_message}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ActivityLogs() {
  const { tenant } = useTenant();
  const tenantId = tenant?.id;

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedEmail, setExpandedEmail] = useState(null);

  // const [deleting, setDeleting] = useState(null);

  const [filters, setFilters] = useState({
    email: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });

  const fetchPatients = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = { tenantId, page, limit: 25 };
      if (filters.email) params.email = filters.email;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const res = await api.get('/api/activity-log/admin/sessions', { params });
      setPatients(res.data.sessions || []);
      setTotalPages(res.data.pagination?.pages || 1);
    } catch {
      setError('Failed to load activity logs');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, filters]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
    setExpandedEmail(null);
  };

  // --- DELETE HANDLERS (uncomment to re-enable) ---
  // const handleDeleteLogs = async (email, e) => {
  //   e.stopPropagation();
  //   if (!window.confirm(`Delete all logs for ${maskEmail(email)}?`)) return;
  //   setDeleting(email);
  //   try {
  //     await api.delete('/api/activity-log/admin/logs', { data: { tenantId, email } });
  //     fetchPatients();
  //   } catch {
  //     alert('Failed to delete logs');
  //   } finally {
  //     setDeleting(null);
  //   }
  // };
  //
  // const handleDeleteAllLogs = async () => {
  //   if (!window.confirm('Delete ALL activity logs? This cannot be undone.')) return;
  //   setDeleting('__all__');
  //   try {
  //     await api.delete('/api/activity-log/admin/logs/all', { data: { tenantId } });
  //     fetchPatients();
  //   } catch {
  //     alert('Failed to delete logs');
  //   } finally {
  //     setDeleting(null);
  //   }
  // };
  // --- END DELETE HANDLERS ---

  const getPatientStatus = (p) => {
    const lastT = p.last_activity ? new Date(p.last_activity).getTime() : 0;
    const submitT = p.last_submit_time ? new Date(p.last_submit_time).getTime() : 0;
    const abandonT = p.last_abandon_time ? new Date(p.last_abandon_time).getTime() : 0;

    if (submitT && submitT >= lastT) return { label: 'Submitted', color: '#4caf50', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
    if (abandonT && abandonT >= lastT) return { label: 'Abandoned', color: '#9e9e9e', icon: <ExitToAppIcon sx={{ fontSize: 16 }} /> };
    if (lastT > abandonT || lastT > submitT) return { label: 'In Progress', color: '#ff9800', icon: <WarningIcon sx={{ fontSize: 16 }} /> };
    if (Number(p.error_count) > 0) return { label: 'Has Errors', color: '#f44336', icon: <ErrorIcon sx={{ fontSize: 16 }} /> };
    return { label: 'In Progress', color: '#ff9800', icon: <WarningIcon sx={{ fontSize: 16 }} /> };
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Activity Logs</Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search Email"
          size="small"
          value={filters.email}
          onChange={(e) => handleFilterChange('email', e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="From"
          type="date"
          size="small"
          value={filters.dateFrom}
          onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filters.dateTo}
          onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        {/* --- CLEAR ALL LOGS BUTTON (uncomment to re-enable) ---
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={deleting === '__all__' ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
          disabled={!!deleting || patients.length === 0}
          onClick={handleDeleteAllLogs}
          sx={{ ml: 'auto' }}
        >
          Clear All Logs
        </Button>
        --- END CLEAR ALL LOGS BUTTON --- */}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, width: 40 }}></TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Patient</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Sessions</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Errors</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>First Seen</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Last Activity</TableCell>
              {/* <TableCell sx={{ fontWeight: 600, width: 60 }}>Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {tenantId ? 'No activity logs found' : 'Loading tenant...'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              patients.map((patient) => {
                const status = getPatientStatus(patient);
                const isExpanded = expandedEmail === patient.email;
                return (
                  <React.Fragment key={patient.email}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& td': { borderBottom: isExpanded ? 'none' : undefined } }}
                      onClick={() => setExpandedEmail(isExpanded ? null : patient.email)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{patient.patient_name || '—'}</TableCell>
                      <TableCell>{maskEmail(patient.email)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${patient.session_count} ${Number(patient.session_count) === 1 ? 'session' : 'sessions'}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        {Number(patient.error_count) > 0 ? (
                          <Tooltip title={patient.last_error || ''}>
                            <Chip
                              label={patient.error_count}
                              size="small"
                              sx={{ backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 600 }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          size="small"
                          sx={{
                            backgroundColor: status.color + '20',
                            color: status.color,
                            fontWeight: 500,
                            '& .MuiChip-icon': { color: status.color },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmtDate(patient.first_seen)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmtDate(patient.last_activity)}</TableCell>
                      {/* --- PER-ROW DELETE BUTTON (uncomment to re-enable) ---
                      <TableCell>
                        <Tooltip title="Delete logs for this patient">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!!deleting}
                            onClick={(e) => handleDeleteLogs(patient.email, e)}
                          >
                            {deleting === patient.email ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      --- END PER-ROW DELETE BUTTON --- */}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <PatientTimeline email={patient.email} tenantId={tenantId} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => { setPage(v); setExpandedEmail(null); }}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
